import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStudyQuestions, getWeaknessHeatmap } from '../utils/studyDataService';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from './LoadingSpinner';

const MapaCalorDebilidades = () => {
  const navigate = useNavigate();
  const { profileData } = useAuth();
  const [heatmapData, setHeatmapData] = useState([]);
  const [filterStatus, setFilterStatus] = useState('ALL'); // ALL, RED, YELLOW, GREEN
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const questions = await loadStudyQuestions(profileData);
      const heatmap = getWeaknessHeatmap(questions);
      setHeatmapData(heatmap);
      setLoading(false);
    }
    loadData();
  }, [profileData]);

  const totalMaterias = heatmapData.length;
  const redCount = heatmapData.filter(m => m.status === 'RED').length;
  const yellowCount = heatmapData.filter(m => m.status === 'YELLOW').length;
  const greenCount = heatmapData.filter(m => m.status === 'GREEN').length;

  // Filtrado por botón de semáforo
  const filteredData = heatmapData.filter(m => {
    if (filterStatus === 'ALL') return true;
    return m.status === filterStatus;
  });

  if (loading) return <LoadingSpinner message="Calculando tu Diagnóstico y Mapa de Debilidades EXANI-II..." />;

  return (
    <div style={{
      padding: '1.5rem',
      color: 'var(--text-primary)',
      height: 'calc(100vh - 60px)',
      boxSizing: 'border-box',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    }}>
      {/* Encabezado Táctico */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        borderBottom: '1px solid rgba(197, 160, 89, 0.2)',
        paddingBottom: '1rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h1 style={{
            color: '#c5a059',
            fontFamily: "'Orbitron', sans-serif",
            fontSize: '1.25rem',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            📊 Diagnóstico y Mapa de Debilidades
          </h1>

          <span style={{
            fontSize: '0.75rem',
            padding: '4px 12px',
            borderRadius: '12px',
            background: 'rgba(197, 160, 89, 0.15)',
            color: '#c5a059',
            border: '1px solid #c5a059',
            fontWeight: 'bold',
            fontFamily: "'Orbitron', sans-serif"
          }}>
            Semáforo por Módulo y Tema
          </span>
        </div>

        <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(181, 200, 216, 0.8)' }}>
          Monitorea tu nivel de retención acumulado. Focaliza tu estudio directamente en las materias marcadas en rojo para optimizar tu preparación para el EXANI-II.
        </p>
      </div>

      {/* Tarjetas de Resumen General */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '12px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #0d2035 0%, #071526 100%)',
          border: '1px solid rgba(197, 160, 89, 0.3)',
          borderRadius: '10px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}>
          <span style={{ fontSize: '0.7rem', color: 'rgba(181, 200, 216, 0.7)', textTransform: 'uppercase', fontFamily: "'Orbitron', sans-serif" }}>
            Total Materias Eval
          </span>
          <span style={{ fontSize: '1.6rem', color: '#fff', fontWeight: 'bold' }}>{totalMaterias}</span>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #450a0a 0%, #200505 100%)',
          border: '1px solid #ef4444',
          borderRadius: '10px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          boxShadow: '0 4px 16px rgba(239, 68, 68, 0.15)'
        }}>
          <span style={{ fontSize: '0.7rem', color: '#fca5a5', textTransform: 'uppercase', fontFamily: "'Orbitron', sans-serif" }}>
            🔴 Refuerzo Urgente (&lt;60%)
          </span>
          <span style={{ fontSize: '1.6rem', color: '#f87171', fontWeight: 'bold' }}>{redCount} Materias</span>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #451a03 0%, #220d02 100%)',
          border: '1px solid #f59e0b',
          borderRadius: '10px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          boxShadow: '0 4px 16px rgba(245, 158, 11, 0.15)'
        }}>
          <span style={{ fontSize: '0.7rem', color: '#fde68a', textTransform: 'uppercase', fontFamily: "'Orbitron', sans-serif" }}>
            🟡 En Progreso (60-84%)
          </span>
          <span style={{ fontSize: '1.6rem', color: '#fbbf24', fontWeight: 'bold' }}>{yellowCount} Materias</span>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #022c22 0%, #011913 100%)',
          border: '1px solid #10b981',
          borderRadius: '10px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          boxShadow: '0 4px 16px rgba(16, 185, 129, 0.15)'
        }}>
          <span style={{ fontSize: '0.7rem', color: '#a7f3d0', textTransform: 'uppercase', fontFamily: "'Orbitron', sans-serif" }}>
            🟢 Dominadas (≥85%)
          </span>
          <span style={{ fontSize: '1.6rem', color: '#34d399', fontWeight: 'bold' }}>{greenCount} Materias</span>
        </div>
      </div>

      {/* Filtros de Semáforo */}
      <div style={{
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        background: 'rgba(10, 26, 42, 0.8)',
        padding: '10px 14px',
        borderRadius: '8px',
        border: '1px solid rgba(197, 160, 89, 0.2)'
      }}>
        <button
          onClick={() => setFilterStatus('ALL')}
          style={{
            background: filterStatus === 'ALL' ? '#c5a059' : 'rgba(255,255,255,0.05)',
            color: filterStatus === 'ALL' ? '#071526' : '#fff',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 14px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            fontFamily: "'Orbitron', sans-serif",
            cursor: 'pointer'
          }}
        >
          Todas ({totalMaterias})
        </button>

        <button
          onClick={() => setFilterStatus('RED')}
          style={{
            background: filterStatus === 'RED' ? '#ef4444' : 'rgba(239, 68, 68, 0.12)',
            color: filterStatus === 'RED' ? '#fff' : '#f87171',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            padding: '8px 14px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            fontFamily: "'Orbitron', sans-serif",
            cursor: 'pointer'
          }}
        >
          🔴 Críticas ({redCount})
        </button>

        <button
          onClick={() => setFilterStatus('YELLOW')}
          style={{
            background: filterStatus === 'YELLOW' ? '#f59e0b' : 'rgba(245, 158, 11, 0.12)',
            color: filterStatus === 'YELLOW' ? '#071526' : '#fbbf24',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            padding: '8px 14px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            fontFamily: "'Orbitron', sans-serif",
            cursor: 'pointer'
          }}
        >
          🟡 En Progreso ({yellowCount})
        </button>

        <button
          onClick={() => setFilterStatus('GREEN')}
          style={{
            background: filterStatus === 'GREEN' ? '#10b981' : 'rgba(16, 185, 129, 0.12)',
            color: filterStatus === 'GREEN' ? '#071526' : '#34d399',
            border: '1px solid #10b981',
            borderRadius: '6px',
            padding: '8px 14px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            fontFamily: "'Orbitron', sans-serif",
            cursor: 'pointer'
          }}
        >
          🟢 Dominadas ({greenCount})
        </button>
      </div>

      {/* Grid de Materias / Diagnóstico */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '16px'
      }}>
        {filteredData.map((item, idx) => (
          <div
            key={idx}
            style={{
              background: 'linear-gradient(145deg, #0d2035 0%, #071526 100%)',
              border: `1px solid ${item.badgeColor}`,
              borderTop: `4px solid ${item.badgeColor}`,
              borderRadius: '12px',
              padding: '18px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '14px',
              boxShadow: `0 6px 20px rgba(0,0,0,0.5)`,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Cabecera Materia */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                <h3 style={{
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  fontWeight: '700',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  {item.materia}
                </h3>

                <span style={{
                  fontSize: '0.65rem',
                  fontWeight: 'bold',
                  fontFamily: "'Orbitron', sans-serif",
                  color: '#fff',
                  background: item.badgeColor,
                  padding: '3px 8px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap'
                }}>
                  {item.statusText}
                </span>
              </div>

              <div style={{ fontSize: '0.75rem', color: 'rgba(181, 200, 216, 0.7)', marginBottom: '12px' }}>
                {item.totalQuestions} preguntas registradas en banco
              </div>

              {/* Barra de Progreso % */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                  <span style={{ color: 'rgba(181, 200, 216, 0.8)' }}>Dominio Estimado:</span>
                  <span style={{ color: item.badgeColor, fontWeight: 'bold' }}>{item.percentage}%</span>
                </div>

                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${item.percentage}%`,
                    height: '100%',
                    background: item.badgeColor,
                    borderRadius: '4px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            </div>

            {/* Acciones Directas */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '6px',
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              paddingTop: '12px'
            }}>
              <button
                onClick={() => navigate('/dashboard/flashcards')}
                style={{
                  background: 'rgba(197, 160, 89, 0.1)',
                  border: '1px solid rgba(197, 160, 89, 0.3)',
                  borderRadius: '6px',
                  color: '#c5a059',
                  padding: '8px 4px',
                  fontSize: '0.68rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px'
                }}
              >
                <span>📇</span>
                <span>Flashcards</span>
              </button>

              <button
                onClick={() => navigate('/dashboard/modo-guardia')}
                style={{
                  background: 'rgba(96, 165, 250, 0.1)',
                  border: '1px solid rgba(96, 165, 250, 0.3)',
                  borderRadius: '6px',
                  color: '#60a5fa',
                  padding: '8px 4px',
                  fontSize: '0.68rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px'
                }}
              >
                <span>🎧</span>
                <span>Guardia</span>
              </button>

              <button
                onClick={() => navigate('/dashboard/quiz')}
                style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '6px',
                  color: '#34d399',
                  padding: '8px 4px',
                  fontSize: '0.68rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px'
                }}
              >
                <span>📋</span>
                <span>Quiz</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MapaCalorDebilidades;
